import React from 'react'
import {shareErrorMessage} from './errors'
import {describeActivity, formatShareDateTime} from './format'
import {useShareActivity} from './managementHooks'
import type {ShareMember} from './types'

interface ShareActivityListProps {
  shareId: string
  members: ShareMember[]
}

/** Members' file operations in the folder, newest first (kept for 180 days). */
export const ShareActivityList: React.FC<ShareActivityListProps> = ({shareId, members}) => {
  const activity = useShareActivity(shareId, true)
  const items = activity.data?.pages.flatMap((p) => p.items) ?? []

  const who = (sub: string) => {
    const m = members.find((x) => x.sub === sub)
    return m ? m.name || m.email || 'A member' : 'A former member'
  }

  if (activity.isLoading) {
    return <div className='text-muted small py-3'>Loading activity…</div>
  }
  if (activity.isError) {
    return (
      <div className='alert alert-warning small mb-0' role='alert'>
        {shareErrorMessage(activity.error)}
      </div>
    )
  }
  if (items.length === 0) {
    return (
      <p className='text-muted small mb-0 py-2'>
        No file activity yet. Uploads, new folders, deletions, moves and copies show up here for 180
        days.
      </p>
    )
  }
  return (
    <>
      <ul className='list-unstyled mb-0 ob-sharing-list' aria-label='Activity'>
        {items.map((item, i) => (
          <li
            key={`${item.at}-${i}`}
            className='ob-sharing-list__row'
            data-testid='sharing-activity-row'
          >
            <div className='ob-sharing-list__main'>
              <div className='text-break'>
                <span className='fw-semibold'>{who(item.sub)}</span> {describeActivity(item)}
              </div>
              <div className='text-muted small'>{formatShareDateTime(item.at)}</div>
            </div>
          </li>
        ))}
      </ul>
      {activity.hasNextPage && (
        <button
          type='button'
          className='btn btn-sm btn-light mt-2'
          onClick={() => activity.fetchNextPage()}
          disabled={activity.isFetchingNextPage}
        >
          {activity.isFetchingNextPage ? 'Loading…' : 'Show older activity'}
        </button>
      )}
    </>
  )
}
